import React, { useState } from 'react';
import { Users, Briefcase, Package, FileText, Mail, Phone, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

// Team data with contact info. Add email/phone to enable them in the dialog.
const owners = [
  { name: 'Paul Comalada', role: 'Co-Owner', email: 'Paul@winecollectionspirits.com', phone: '+1 (786) 359-6478' },
  { name: 'Eric Cravello', role: 'Co-Owner', email: 'Eric_Cravello@msn.com', phone: '+1 (305) 305-0568' },
];

const accountManagers = [
  { name: 'Boris Renou', role: 'Account Manager', email: 'Renou.Boris@gmail.com', phone: '+1 (786) 312-7532' },
  { name: 'Scott Smith', role: 'Account Manager', email: 'smith-mscott@wg-wc.com', phone: '+1 (404) 925-7943' },
  { name: 'Yentzu Chen', role: 'Account Manager', email: 'Yentzu@winecollectionspirits.com', phone: '+1 (917) 361-4673' },
  { name: 'Greg Cekay', role: 'Account Manager', email: 'Greg@winecollectionspirits.com', phone: '+1 (786) 478-7673' },
];

const warehouseManagers = [
  { name: 'Alejandro Vernier', role: 'Warehouse Manager' },
  { name: 'Franck Soler', role: 'Warehouse Manager' },
];

const officeManagers = [
  { name: 'Cleiby Calderon', role: 'Office Manager' },
];

const PersonCard = ({ person, onClick, variant = 'default' }) => {
  const hasContact = person.email || person.phone;
  const baseClasses = variant === 'owner'
    ? 'bg-white border border-gray-900 p-6 min-w-[200px] text-center cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5'
    : 'bg-white border border-gray-200 p-4 cursor-pointer transition-all hover:border-gray-900 hover:shadow-md';

  return (
    <button
      type="button"
      onClick={() => onClick(person)}
      className={baseClasses}
      data-testid={`person-card-${person.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {variant === 'owner' ? (
        <>
          <h3 className="text-lg font-light text-gray-900">{person.name}</h3>
          <p className="text-sm text-gray-600 font-light mt-1">{person.role}</p>
          <p className="text-xs text-gray-400 font-light mt-3 uppercase tracking-wider">
            {hasContact ? 'View contact' : 'View details'}
          </p>
        </>
      ) : (
        <p className="text-sm font-light text-gray-900">{person.name}</p>
      )}
    </button>
  );
};

const Management = () => {
  const [selectedPerson, setSelectedPerson] = useState(null);
  const open = Boolean(selectedPerson);

  const handleClose = () => setSelectedPerson(null);
  const handleOpen = (person) => setSelectedPerson(person);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="management-page">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-light text-gray-900 mb-6">Our Management Team</h1>
            <p className="text-xl text-gray-600 font-light">
              Experienced professionals dedicated to excellence in wine and spirits distribution
            </p>
            <p className="text-sm text-gray-500 font-light mt-4 italic">
              Click on any team member to view contact information
            </p>
          </div>
        </div>
      </section>

      {/* Organizational Chart */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Owners - Top Level */}
          <div className="flex justify-center mb-16">
            <div className="text-center">
              <div className="inline-block bg-gray-900 text-white px-8 py-6 mb-4">
                <Users size={32} className="mx-auto mb-3" strokeWidth={1} />
                <h2 className="text-xl font-light mb-2">Owners</h2>
              </div>
              <div className="grid grid-cols-2 gap-6 mt-8">
                {owners.map((person) => (
                  <PersonCard key={person.name} person={person} onClick={handleOpen} variant="owner" />
                ))}
              </div>
            </div>
          </div>

          {/* Connecting Line */}
          <div className="flex justify-center mb-8">
            <div className="w-px h-12 bg-gray-300"></div>
          </div>

          {/* Department Heads */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Account Managers */}
            <div className="text-center">
              <div className="bg-white border border-gray-300 p-6 mb-6">
                <Briefcase size={28} className="mx-auto mb-3 text-gray-900" strokeWidth={1} />
                <h3 className="text-xl font-light text-gray-900">Account Managers</h3>
              </div>
              <div className="space-y-3">
                {accountManagers.map((person) => (
                  <PersonCard key={person.name} person={person} onClick={handleOpen} />
                ))}
              </div>
            </div>

            {/* Warehouse Managers */}
            <div className="text-center">
              <div className="bg-white border border-gray-300 p-6 mb-6">
                <Package size={28} className="mx-auto mb-3 text-gray-900" strokeWidth={1} />
                <h3 className="text-xl font-light text-gray-900">Warehouse Managers</h3>
              </div>
              <div className="space-y-3">
                {warehouseManagers.map((person) => (
                  <PersonCard key={person.name} person={person} onClick={handleOpen} />
                ))}
              </div>
            </div>

            {/* Office Manager */}
            <div className="text-center">
              <div className="bg-white border border-gray-300 p-6 mb-6">
                <FileText size={28} className="mx-auto mb-3 text-gray-900" strokeWidth={1} />
                <h3 className="text-xl font-light text-gray-900">Office Manager</h3>
              </div>
              <div className="space-y-3">
                {officeManagers.map((person) => (
                  <PersonCard key={person.name} person={person} onClick={handleOpen} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-6">Our Expertise</h2>
            <p className="text-lg text-gray-600 font-light leading-relaxed">
              Our management team brings decades of combined experience in the wine and spirits industry.
              From sourcing premium European selections to managing complex logistics and ensuring
              regulatory compliance, our dedicated professionals work together to deliver exceptional
              service to our clients across Florida.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="text-4xl font-light text-gray-900 mb-2">{owners.length}</div>
              <p className="text-gray-600 font-light">Owners</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-gray-900 mb-2">{accountManagers.length}</div>
              <p className="text-gray-600 font-light">Account Managers</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-gray-900 mb-2">
                {warehouseManagers.length + officeManagers.length}
              </div>
              <p className="text-gray-600 font-light">Operations Staff</p>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">Our Facility</h2>
          <p className="text-lg text-gray-600 font-light mb-8">
            Based in our 4,500 sq. ft. temperature-controlled warehouse in Miami, our team manages
            every aspect of wine and spirits distribution with precision and care.
          </p>
          <div className="bg-white border border-gray-200 p-8 inline-block">
            <p className="text-gray-900 font-light">7227 NW 32 street Miami FL 33122</p>
            <p className="text-gray-600 font-light mt-2">+1 (786) 359-6478</p>
          </div>
        </div>
      </section>

      {/* Contact Info Dialog */}
      <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
        <DialogContent className="sm:max-w-md" data-testid="person-dialog">
          {selectedPerson && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-light text-gray-900">
                  {selectedPerson.name}
                </DialogTitle>
                <DialogDescription className="text-gray-600 font-light">
                  {selectedPerson.role}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 space-y-4">
                {selectedPerson.email ? (
                  <a
                    href={`mailto:${selectedPerson.email}`}
                    className="flex items-center gap-3 p-4 border border-gray-200 hover:border-gray-900 transition-colors group"
                    data-testid="person-email-link"
                  >
                    <Mail size={20} className="text-gray-600 group-hover:text-gray-900" strokeWidth={1.5} />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-light">Email</p>
                      <p className="text-gray-900 font-light break-all">{selectedPerson.email}</p>
                    </div>
                  </a>
                ) : null}

                {selectedPerson.phone ? (
                  <a
                    href={`tel:${selectedPerson.phone.replace(/[^+\d]/g, '')}`}
                    className="flex items-center gap-3 p-4 border border-gray-200 hover:border-gray-900 transition-colors group"
                    data-testid="person-phone-link"
                  >
                    <Phone size={20} className="text-gray-600 group-hover:text-gray-900" strokeWidth={1.5} />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-light">Phone</p>
                      <p className="text-gray-900 font-light">{selectedPerson.phone}</p>
                    </div>
                  </a>
                ) : null}

                {!selectedPerson.email && !selectedPerson.phone && (
                  <div className="p-6 bg-gray-50 border border-gray-200 text-center">
                    <p className="text-gray-600 font-light">
                      Contact information not available yet.
                    </p>
                    <p className="text-sm text-gray-500 font-light mt-2">
                      Please reach out to our main office at{' '}
                      <a href="mailto:Paul@winecollectionspirits.com" className="underline hover:text-gray-900">
                        Paul@winecollectionspirits.com
                      </a>{' '}
                      or +1 (786) 359-6478.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Management;
